import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { createAddressFieldPath, hasStringValue } from "./helpers/addressUtils";

const useStyles = makeStyles((theme) => ({
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.03)",
    },
    borderRadius: 6,
    overflow: "hidden",
  },
  th: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    borderBottom: "none",
    color: theme.palette.text.secondary,
  },
  labelCell: {
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  cell: {
    fontSize: 12,
    position: "relative",
    cursor: "text",
    paddingRight: 32,
    whiteSpace: "nowrap",
  },
  editBtn: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
    opacity: 0,
    transition: "opacity 120ms ease",
  },
  cellHover: {
    "&:hover $editBtn": {
      opacity: 1,
    },
  },
  input: {
    fontSize: 12,
  },
  empty: {
    opacity: 0.4,
    fontStyle: "italic",
  },
  dirty: {
    backgroundColor: "rgba(255, 193, 7, 0.12)",
    boxShadow: "inset 3px 0 0 #f7bd02",
  },
}));

const AddressCompareTable = ({
  title,
  columns,
  editable,
  isFieldDirty,
  onChangeCell,
}) => {
  const classes = useStyles();
  const intl = useIntl();

  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");

  // ---------------------------
  // ROW BUILDING
  // ---------------------------
  const rows = useMemo(() => {
    const map = new Map();

    columns.forEach((col) => {
      const section = col.editType;
      const list = Array.isArray(col.address) ? col.address : [];

      list.forEach((item, index) => {
        const rowId = `${item.field}__${item.label}`;

        if (!map.has(rowId)) {
          map.set(rowId, {
            rowId,
            field: item.field,
            label: item.label,
            values: {},
            positions: [],
          });
        }

        const row = map.get(rowId);

        row.values[section] = item.value;
        row.positions.push(index);
      });
    });

    return Array.from(map.values()).sort((a, b) => {
      const avgA = a.positions.reduce((x, y) => x + y, 0) / a.positions.length;
      const avgB = b.positions.reduce((x, y) => x + y, 0) / b.positions.length;
      return avgA - avgB;
    });
  }, [columns]);

  // ---------------------------
  // EDITING (FIXED)
  // ---------------------------
  const startEdit = (section, rowId, value) => {
    if (!editable || value === undefined || value === null) return;

    setEditing({ section, rowId });
    setDraft(value ?? "");
  };

  const commit = () => {
    if (!editing) return;

    const { section, rowId } = editing;
    const trimmed = String(draft ?? "").trim();

    const row = rows.find(r => r.rowId === rowId);
    if (!row) return;

    onChangeCell?.(section, row.field, trimmed);

    setEditing(null);
    setDraft("");
  };

  const cancel = () => {
    setEditing(null);
    setDraft("");
  };

  if (!rows.length) return null;

  return (
    <Table className={classes.table} size="small">
      <TableHead>
        <TableRow>
          <TableCell className={classes.th}>
            <Typography className={classes.th}>{title}</Typography>
          </TableCell>

          {columns.map((col) => (
            <TableCell key={col.editType} className={classes.th}>
              {col.title}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.rowId}>
            <TableCell className={classes.labelCell}>
              {row.label}
            </TableCell>

            {columns.map((col) => {
              const section = col.editType;
              const value = row.values[section];
                            
              const hasValue = value !== undefined && value !== null;

              const isEditing =
                editing?.section === section &&
                editing?.rowId === row.rowId;

              const isDirty =
                editable &&
                hasStringValue(value) &&
                isFieldDirty?.(
                  createAddressFieldPath(section, row.field)
                );

              return (
                <TableCell
                  key={section}
                  className={clsx(classes.cell, isDirty && classes.dirty)}
                  onClick={() => {
                    if (!editable || !hasValue) return;
                    startEdit(section, row.rowId, value);
                  }}
                >
                  {editable && !isEditing && hasValue && (
                    <Tooltip title={intl.formatMessage({ id: "incomingOrders.addresses.edit" })}>
                      <IconButton
                        size="small"
                        className={classes.editBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(section, row.rowId, value);
                        }}
                      >
                        <EditOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {editable && isEditing ? (
                    <TextField
                      autoFocus
                      variant="standard"
                      fullWidth
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={commit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commit();
                        if (e.key === "Escape") cancel();
                      }}
                      InputProps={{ className: classes.input }}
                    />
                  ) : hasStringValue(value) ? (
                    value
                  ) : (
                        <span className={classes.empty}>—</span>
                      )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

AddressCompareTable.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  isFieldDirty: PropTypes.func,
  onChangeCell: PropTypes.func,
};

export default AddressCompareTable;